import { useTranslation } from "react-i18next";
import { localeMoment } from "../../helpers";
import {
  CalendarIcon,
  ClockIcon,
  ChevronLeftIcon,
  TrashIcon,
} from "@heroicons/react/solid";
import SessionModel from "../../api/models/SessionModel";
import { useEffect, useMemo, useState } from "react";
import UserProfilePictureAvatar from "../avatar/UserProfilePictureAvatar";
import CalendarScheduleSlotModel from "../../models/CalendarScheduleSlotModel";
import { useTelawah } from "../../hooks/useTelawah";
import { useAuth } from "../../context/AuthContext";
import Modal from "../modal/ModalWithFooter";
import { getSessionState } from "./helper";
import { showTutorSessions } from "../../api/endpoints/tutors/sessions";
import { showStudentSessions } from "../../api/endpoints/students/sessions";
import { SlotStates } from "../../api/models/ScheduleSlotModel";
type Props = {
  session: CalendarScheduleSlotModel;
  onClose?: () => void;
  cancel: boolean;
  setIsOpen: () => void;
  open: boolean;
};

export default function WeeklySessionInfo({
  session,
  onClose,
  open,
  cancel,
  setIsOpen,
}: Props) {
  const LocalBasePath = "page.sessionInfo";
  const [sessionData, setSessionData] = useState<SessionModel | any>();
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const { getTelawahById } = useTelawah();
  const { currentUser, userType }: any = useAuth();
  const sessionInfo = (session: SessionModel | any) => {
    if (session?.student) {
      return session?.student;
    }
    return session?.tutor ?? {};
  };
  const getSession = async (sessionId: number | string) => {
    let UserSessionsApi: any;
    if (userType === "tutor") {
      UserSessionsApi = await showTutorSessions(sessionId);
    } else {
      UserSessionsApi = await showStudentSessions(sessionId);
    }
    if (UserSessionsApi?.isSuccess) {
      let sessionInfo = new SessionModel(UserSessionsApi.data);
      setSessionData(sessionInfo);
    }
  };
  const sessionDetails = useMemo(() => {
    if (!!sessionData) {
      return sessionData;
    } else {
      return session;
    }
  }, [session, sessionData]);

  const state = () => {
    switch (session?.state) {
      case SlotStates.not_started:
        return "scheduled";
      case SlotStates.done:
        return "done";
      case SlotStates.upcoming:
        return "upcoming";
      case SlotStates.in_progress:
        return "in_progress";
      case SlotStates.canceled:
        return "canceled";
      case SlotStates.canceled_by_student:
        return "canceled_by_student";
      case SlotStates.canceled_by_tutor:
        return "canceled_by_tutor";
      case SlotStates.tutor_absence:
        return "tutor_absence";
      case SlotStates.student_absence:
        return "student_absence";
      case SlotStates.terminated_early:
        return "terminated_early";
      case SlotStates.disconnected:
        return "disconnected";
      case SlotStates.disconnected_by_system:
        return "disconnected_by_system";
    }
  };
  useEffect(() => {
    if (!!session?.sessionId && open) {
      getSession(session?.sessionId);
    }
  }, [session, open]);
  return (
    <Modal className="max-w-[360px]" open={open} setOpen={onClose}>
      <Modal.Body
        className={"border-b border-gray-200 pb-4"}
        setOpen={onClose}
        title={t("sessionInfo")}
      >
        <div className="bg-white h-full   ">
          <div className="h-full overflow-y-auto overflow-x-hidden  md:mx-0">
            <div className="w-full flex flex-col">
              <div className="w-full flex flex-col mb-8">
                <div className="w-full mb-0.5 flex justify-between items-center">
                  <p className=" w-full text-gray-700 text-xl font-medium">
                    {sessionDetails?.scheduleSlotType !== "reading"
                      ? t("session") + "# " + sessionDetails?.sessionNumber ??
                        ""
                      : "# " + t("sessionRate")}
                  </p>
                  <div>
                    {getSessionState(
                      `${
                        !!sessionDetails?.status
                          ? sessionDetails?.status
                          : state()
                      }`
                    )}
                  </div>
                </div>

                <div className="w-full  mt-0.5 flex gap-2 justify-between">
                  <div className="flex  items-center">
                    <div className=" text-gray-400 ltr:mr-2 rtl:ml-2">
                      {<CalendarIcon className="w-5 h-5" />}
                    </div>
                    <div className="text-gray-500 text-xs leading-5">
                      {localeMoment(sessionDetails?.date!).format(
                        "dddd DD/MM/YYYY"
                      )}
                    </div>
                  </div>
                  <div className=" p-1  flex-1 flex items-center ">
                    <div className=" text-gray-400 px-2">
                      {<ClockIcon className="w-5 h-5" />}
                    </div>
                    <div className="text-gray-500 text-xs leading-5 ">
                      {localeMoment(
                        !!sessionDetails?.start_time
                          ? sessionDetails?.start_time
                          : sessionDetails?.startTime!
                      ).format("hh:mm")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 space-x-4 mb-6">
                <UserProfilePictureAvatar
                  profilePictureUrl={
                    sessionInfo(sessionDetails)?.profile_picture_url
                  }
                  className="w-14 h-14 flex-shrink-0"
                  alt={sessionInfo(sessionDetails)?.name}
                />
                <div>
                  <p className="text-gray-600 text-sm font-medium">
                    {sessionInfo(sessionDetails)?.name}
                  </p>
                  <p className="text-xs leading-4 text-gray-500 ">
                    {sessionInfo(sessionDetails)?.account?.email ??
                      sessionInfo(sessionDetails)?.email}
                  </p>
                </div>
              </div>

              <div className="w-full  border-b mb-2  border-gray-200 flex flex-col items-start  py-4">
                <div>
                  <p className="text-gray-500">{t("reading")}</p>
                </div>
                <div>
                  <div className="text-gray-600">
                    <span className="flex justify-start gap-1">
                      <span>
                        <h1>
                          {getTelawahById(
                            +sessionDetails?.student?.telawah_id! as number
                          )?.[0]?.name ??
                            getTelawahById(+currentUser?.telawah_id)?.[0]?.name}
                        </h1>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex ">
          {cancel && (
            <button
              type="button"
              className=" inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 border-red-600 bg-transparent text-red-600 hover:text-white  hover:bg-red-700 disabled:opacity-80 disabled:hover:bg-red-600 focus:ring-red-500"
              onClick={() => !!setIsOpen && setIsOpen()}
            >
              <div className="flex gap-2">
                <TrashIcon className="w-5 h-5" />
                {t("cancelSession")}
              </div>
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
